import { AxiosResponse } from 'axios';
import { FIELD_TYPES } from '../components/fastForm/data/constants';
import { CurrencyCode } from '../types/currencyCode';
import { EntitySectionDefinitionResponse } from '../types/entityDefinitions';
import { EntityType } from '../util/entities';
import { getApiUrl } from '../util/util';
import { get } from './http';

export interface ProductConfigurationResponse {
	name: string;
	policyConfiguration: PolicyConfiguration;
	claimConfiguration?: ClaimConfiguration;
	premiumReportingConfiguration?: PremiumReportingConfiguration[];
	configVersion?: string;
}

export interface CashManagementConfigurationResponse {
	version: string;
	configuration: CashManagementConfiguration;
}

export interface CashManagementConfiguration {
	cashTransfers: CashTransferConfiguration[];
	disbursement: DisbursementConfiguration;
	reversals: PaymentReversalConfiguration[];
}

export interface DisbursementConfiguration {
	fields: FieldConfiguration[];
}

export interface CashTransferConfiguration {
	name: string;
	displayName: string;
	requireDetails: boolean;
}

export interface PaymentReversalConfiguration {
	name: string;
	displayName: string;
	requireDetails: boolean;
}

export interface PolicyConfiguration {
	cancellations: CancellationConfigurationResponse;
	documents: PolicyDocumentationConfiguration[];
	endorsements: EndorsementConfigurationResponse[];
	exposures: ExposureConfigurationResponse[];
	externalDataCalls: FrontendExternalDataCallConfiguration[];
	fees: FeeConfigurationResponse[];
	fields: FieldConfiguration[];
	paymentSchedules: PaymentScheduleResponse[];
	plugins: ProductScriptingConfig;
	reminders: ReminderConfigurationResponse[];
	renewals: RenewalConfigurationResponse[];
	taxes: TaxConfigurationResposne[];
	defaultPaymentTerms?: PaymentTermResponse;
	externalRater?: string;
	lapse?: LapseConfigurationResponse;
}

export interface ClaimConfiguration {
	fields: FieldConfiguration[];
	subClaimConfiguration?: SubClaimConfigurationResponse;
}

export interface SubClaimConfigurationResponse {
	fields: FieldConfiguration[];
}

export interface PremiumReportingConfiguration {
	reportName: string;
	tenantLocator: string;
	productRevisionLocator: string;
	premiumReportingCalculationRevision: string;
	id: string;
	fields: FieldConfiguration[];
	documents: PolicyDocumentationConfiguration[];
}

export interface CancellationConfigurationResponse {
	reasons: CancellationReasonConfig[];
}

export interface CancellationReinstatementConfig {
	documents: Document[];
	defaultDeadlineDays: string;
}

export interface CancellationReasonConfig {
	documents: Document[];
	name: string;
	title: string;
	reinstatement: CancellationReinstatementConfig;
	cancellationCategories?: string[];
}

export interface CancellationConfigurationResponse {
	reasons: CancellationReasonConfig[];
}

export interface PolicyDocumentationConfiguration {
	displayName: string;
	fileName: string;
	templateName: string;
	type: 'pdf' | 'html';
}

export interface EndorsementConfigurationResponse {
	displayName: string;
	documents: PolicyDocumentationConfiguration[];
	name: string;
	reprice: 'always' | 'never' | 'input' | 'all';
}

export interface ExposureConfigurationResponse {
	displayName: string;
	externalDataCalls: FrontendExternalDataCallConfiguration[];
	fields: FieldConfiguration[];
	name: string;
	perils: PerilConfigurationResponse[];
}

export interface PerilConfigurationResponse {
	displayName: string;
	externalDataCalls: FrontendExternalDataCallConfiguration[];
	fields: FieldConfiguration[];
	name: string;
	deductible?: number;
	indemnityInAggregate?: string;
	indemnityPerEvent?: string;
	indemnityPerItem?: string;
	lumpSumPayment?: number;
}

export interface FrontendExternalDataCallConfiguration {
	targetFields: string[];
	triggerField: string;
}

export interface FeeConfigurationResponse {
	displayName: string;
	name: string;
}

export type FieldConfiguration =
	| DateFieldConfiguration
	| EmailFieldConfiguration
	| GroupFieldConfiguration
	| LookupFieldConfiguration
	| MediaFieldConfiguration
	| NumberFieldConfiguration
	| SelectFieldConfiguration
	| StringFieldConfiguration
	| CurrencyFieldConfiguration
	| AutocompleteFieldConfiguration;

export interface PolicyholderFieldConfiguration {
	title: string;
	uri: string;
	required: boolean;
	modifiable: boolean;
	width: { [width: string]: string };
}

export interface OntologyResponse {
	id: string;
	tenantId: string;
	timestamp: string;
	items: PolicyholderFieldDetails[];
}

export interface PolicyholderFieldDetails {
	uri: string;
	type: PolicyholderFieldType;
}

export interface PolicyholderFieldType {
	options: PolicyholderFieldTypeOptions;
	uri: string;
}

export interface PolicyholderFieldTypeOptions {
	birthdate: boolean;
	repeatable: boolean;
	searchable: boolean;
	regex?: string;
	multiline?: boolean;
	precision?: string;
	minimumDateTime?: string;
	maximumDateTime?: string;
	maximumOffset?: DateOffsetConfiguration;
	minimumOffset?: DateOffsetConfiguration;
	sequenceId?: string;
	digits?: string;
	minimum?: string;
	values?: string[];
}

export interface DateFieldConfiguration {
	name: string;
	title: string;
	type: string;
	optional?: boolean;
	condition?: { [fieldName: string]: string[] };
	width?: number;
	newline?: boolean;
	heading?: string;

	order: number;
	precision: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second';
	maximum?: string;
	maximumOffset?: DateOffsetConfiguration;
	minimum?: string;
	minimumOffset?: DateOffsetConfiguration;
	showCalendar?: boolean;
	values: string[];
	disabled?: boolean;
	ontologyType?: string;
}

export interface DateOffsetConfiguration {
	amount: string;
	field: 'year' | 'month' | 'day' | 'second';
	roundToDay?: boolean;
}

export interface EmailFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	newline?: boolean;
	optional?: boolean;
	width?: number;
	values: string[];
	regex?: string;
	disabled?: boolean;
	ontologyType?: string;
}

export interface GroupFieldConfiguration {
	fields: FieldConfiguration[];
	name: string;
	order: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	maximum?: string;
	newline?: boolean;
	optional?: boolean;
	repeatable?: boolean;
	width?: number;
	values: string[];
	disabled?: boolean;
}

export interface LookupFieldConfiguration {
	name: string;
	order: number;
	table: string;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	isPublic?: boolean;
	match?: string[];
	newline?: boolean;
	optional?: boolean;
	width?: number;
	values: string[];
	disabled?: boolean;
}

export interface MediaFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	newline?: boolean;
	optional?: boolean;
	repeatable?: boolean;
	width?: number;
	values: string[];
	disabled?: boolean;
}

export interface NumberFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	decimalPlaces?: string;
	heading?: string;
	maximum?: string;
	minimum?: string;
	newline?: boolean;
	optional?: boolean;
	width?: number;
	values: string[];
	disabled?: boolean;
	ontologyType?: string;
}

export interface AutocompleteFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: keyof typeof FIELD_TYPES;
	values: string[];
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	newline?: boolean;
	optional?: boolean;
	repeatable?: boolean;
	width?: number;
	disabled?: boolean;
	ontologyType?: string;
}

export interface SelectFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: string;
	values: string[];
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	newline?: boolean;
	optional?: boolean;
	repeatable?: boolean;
	width?: number;
	disabled?: boolean;
	ontologyType?: string;
}

export interface StringFieldConfiguration {
	name: string;
	order: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	maximum?: string;
	multiline?: boolean;
	newline?: boolean;
	optional?: boolean;
	regex?: string;
	width?: number;
	values: string[];
	disabled?: boolean;
	ontologyType?: string;
}

export interface CurrencyFieldConfiguration {
	name: string;
	order?: number;
	title: string;
	type: string;
	condition?: { [fieldName: string]: string[] };
	heading?: string;
	maximum?: string;
	multiline?: boolean;
	newline?: boolean;
	optional?: boolean;
	regex?: string;
	width?: number;
	values?: string[];
	currency: CurrencyCode;
	disabled?: boolean;
	helperText?: string;
}

export interface PaymentScheduleResponse {
	displayName: string;
	name: string;
	type:
		| 'total'
		| 'monthly'
		| 'annually'
		| 'semiannually'
		| 'quarterly'
		| 'every_two_weeks'
		| 'every_week';
}

export interface ProductScriptingConfig {
	createInstallments: ProductPluginConfig;
	getPerilRates: ProductPluginConfig;
	getProrationResult: ProductPluginConfig;
	getUnderwritingResult: ProductPluginConfig;
}

export interface ProductPluginConfig {
	enabled: boolean;
	isEnabled: boolean;
	path: string;
	type: string; // echo | rater | underwriter | prorater | installments
}

export interface ReminderConfigurationResponse {
	enabled: boolean;
	from: 'policy.end';
	name: string;
	offset: string;
}

export interface RenewalConfigurationResponse {
	documents: PolicyDocumentationConfiguration[];
}

export interface TaxConfigurationResposne {
	displayName: string;
	name: string;
}

export interface PaymentTermResponse {
	amount: number;
	unit: 'day';
}

export interface LapseConfigurationResponse {
	gracePeriodDays: number;
	reinstatementPeriodDays: number;
}

export interface InvoiceConfiguration {
	paymentConfiguration: PaymentConfiguration;
}

export interface PaymentConfiguration {
	fields: FieldConfiguration[];
}

export interface EntityDefinitionResponse {
	id: string;
	type: EntityType;
	idPropertyUri?: string;
	removable?: boolean;
	cancelable?: boolean;
	steps: EntityStepDefinitionResponse[];
}

export interface EntityStepDefinitionResponse {
	title?: string;
	sections: EntitySectionDefinitionResponse[];
}

export interface OntologyDefinitionsResponse {
	id: string;
	items: OntologyItemResponse[];
	timestamp: string;
}

export interface OntologyItemResponse {
	uri: string;
	type: OntologyTypeResponse;
}

export interface OntologyTypeResponse {
	uri: string;
	options: any;
}

export interface TenantConfigurationResponse {
	timezone: string;
	currencyCode: CurrencyCode;
}

export interface CreatePolicyholderWorkflow {
	workflows: Workflow[];
}

export interface Workflow {
	id: string;
	tenant: string;
	timestamp: string;
	message: string;
	value: Value;
}

export interface Value {
	removable?: boolean;
	idPropertyUri: string;
	type: string;
	modifiable: boolean;
	steps: Step[];
}

export interface Step {
	sections: Section[];
}

export interface Section {
	title: string;
	rows: Row[];
}

export interface Row {
	properties: Property[];
}

export interface Property {
	width: Width;
	title: string;
	modifiable: boolean;
	uri: string;
	required: boolean;
	visibleIf?: VisibleIf;
}

export interface Width {
	small: number;
	medium: number;
}

export interface VisibleIf {
	values: string[];
	type: string;
	uri: string;
}

interface ConfigApi {
	getProductConfigs(
		authorizationToken: string,
	): Promise<AxiosResponse<ProductConfigurationResponse>>;
	getCashManagementConfig(
		authorizationToken: string,
	): Promise<AxiosResponse<CashManagementConfigurationResponse>>;
	getV2ProductConfigs(
		tenantId: string,
		authorizationToken: string,
	): Promise<AxiosResponse<ProductConfigurationResponse[]>>;
	getPolicyholderConfig(
		definitionIds: string[],
		authorizationToken: string,
	): Promise<AxiosResponse<EntityDefinitionResponse>>;
	getPaymentConfiguration(
		authorizationToken: string,
	): Promise<AxiosResponse<PaymentConfiguration>>;
	getOntologyDefinitions(
		ontologyId: string,
		authorizationToken: string,
	): Promise<AxiosResponse<OntologyDefinitionsResponse>>;
	getTenantConfiguration(
		authorizationToken: string,
	): Promise<AxiosResponse<TenantConfigurationResponse>>;
}

export default {
	getProductConfigs(authorizationToken: string) {
		return get(`${getApiUrl()}/products`, authorizationToken);
	},

	getCashManagementConfig(authorizationToken: string) {
		return get(`${getApiUrl()}/products/cashmanagement`, authorizationToken);
	},
	//   There are currently 2 endpoints to get product configurations (/products and /product/v2/findByTenant/{tenantId})
	//  /product/v2/findByTenant contains info related to policyholders that are needed which /products does not contain
	getV2ProductConfigs(tenantId: string, authorizationToken: string) {
		return get(
			`${getApiUrl()}/product/v2/findByTenant/${tenantId}`,
			authorizationToken,
		);
	},

	// The item definitions from this endpoint may not be used, but for now will store them in case we find them needed at a later time
	getOntologyDefinitions(ontologyId: string, authorizationToken: string) {
		return get(
			`${getApiUrl()}/ontology/v2/ontology/${ontologyId}`,
			authorizationToken,
		);
	},

	getPaymentConfiguration(authorizationToken: string) {
		return get(`${getApiUrl()}/tenant/configuration`, authorizationToken);
	},

	getPolicyholderConfig(definitionIds: string[], authorizationToken: string) {
		return get(
			`${getApiUrl()}/workflow/v1/workflows/${definitionIds.join(',')}`,
			authorizationToken,
		);
	},

	getTenantConfiguration(authorizationToken: string) {
		return get<TenantConfigurationResponse>(
			`${getApiUrl()}/tenant/tenantConfiguration`,
			authorizationToken,
		);
	},
} as ConfigApi;
